<script setup lang="ts">
import { Statuses } from '@/@types/order'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useForm } from '@inertiajs/vue3'
import { Search } from 'lucide-vue-next'
import { inject, onMounted, ref, watch } from 'vue'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import { DateFormatter, DateValue, getLocalTimeZone, parseDate } from '@internationalized/date'
import { useUrlSearchParams } from '@vueuse/core'
import { CalendarIcon } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    statuses: Statuses
    workshopId: number
}>()

const params = inject('params', useUrlSearchParams())

const form = useForm({
    email_name: String(params.email_name || ''),
    id: String(params.id || ''),
    status: String(params.status || ''),
    order_date: String(params.order_date || ''),
    payment_method: String(params.payment_method || ''),
    group: String(params.group || ''),
})

watch(
    () => form.data(),
    () => {
        params.status = form.status
        params.id = form.id
        params.email_name = form.email_name
        params.order_date = form.order_date
        params.payment_method = form.payment_method
        params.group = form.group
        params.page = '1'

        form.get('/admin/orders?workshop_id=' + props.workshopId, {
            preserveScroll: true,
            only: ['orders', 'pagination', 'status', 'id'],
            preserveState: true,
        })
    },
)

const { locale } = useI18n()

const df = new DateFormatter(locale.value, {
    dateStyle: 'long',
})

const order_date = ref<DateValue>()

watch(order_date, () => {
    if (order_date.value) {
        form.order_date = order_date.value.toString()
    }
})

onMounted(() => {
    order_date.value = form.order_date ? parseDate(form.order_date) : undefined
})
</script>

<template>
    <form class="grid items-center gap-3 lg:grid-cols-3">
        <div class="relative w-full items-center">
            <Input id="email_name" v-model="form.email_name" type="text" placeholder="Pesquisar por e-mail, nome ou empresa" class="pl-8" />
            <span class="absolute inset-y-0 start-0 flex items-center justify-center px-2">
                <Search class="text-muted-foreground size-5" />
            </span>
        </div>
        <Input type="number" v-model="form.id" :placeholder="$t('Order number')" class="w-full rounded-md border border-slate-300 px-3 py-2" />
        <Select v-model="form.status">
            <SelectTrigger class="w-full">
                <SelectValue :placeholder="$t('Order status')" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem :value="null">
                    {{ $t('Order status') }}
                </SelectItem>
                <SelectSeparator />
                <SelectItem v-for="status in statuses" :value="status" :key="status">
                    {{ $t(status) }}
                </SelectItem>
            </SelectContent>
        </Select>

        <Select v-model="form.payment_method">
            <SelectTrigger class="w-full">
                <SelectValue :placeholder="$t('Payment method')" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem :value="null">Método de pagamento</SelectItem>
                <SelectSeparator />

                <SelectItem value="pix">PIX</SelectItem>
                <SelectItem value="credit_card">Cartão de crédito</SelectItem>
                <SelectItem value="bank_slip">Boleto bancário</SelectItem>
                <SelectItem value="company_billing">Faturamento CNPJ</SelectItem>
                <SelectItem value="paypal">Paypal</SelectItem>
            </SelectContent>
        </Select>

        <Select class="w-full" v-model="form.group">
            <SelectTrigger class="w-full">
                <SelectValue placeholder="Grupo do ingresso" />
            </SelectTrigger>

            <SelectContent>
                <SelectItem :value="null">Grupo do ingresso</SelectItem>
                <SelectSeparator />
                <SelectItem value="purchased_ticket">Comprado</SelectItem>
                <SelectItem value="speaker">Palestrante</SelectItem>
                <SelectItem value="sponsor">Patrocinador</SelectItem>
                <SelectItem value="summit_access">Acesso ao Summit</SelectItem>
                <SelectItem value="press">Imprensa</SelectItem>
                <SelectItem value="production">Produção</SelectItem>
                <SelectItem value="exhibitor">Expositor</SelectItem>
                <SelectItem value="courtesy">Cortesia</SelectItem>
                <SelectItem value="staff">Equipe</SelectItem>
                <SelectItem value="supporter">Apoiador</SelectItem>
                <SelectItem value="guest">Convidado</SelectItem>
            </SelectContent>
        </Select>

        <Popover>
            <PopoverTrigger as-child>
                <Button variant="outline" :class="cn('justify-between text-left font-normal', 'text-muted-foreground')">
                    {{ order_date ? df.format(order_date.toDate(getLocalTimeZone())) : 'Selecione uma data' }} <CalendarIcon class="mr-2 h-4 w-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-auto p-0">
                <Calendar v-model="order_date" initial-focus />
            </PopoverContent>
        </Popover>
    </form>
</template>
